import type { CreateTimeSlotRequest } from '../../../types';
import { getTimezoneAbbreviation } from '../../../utils/timezone';
import type { FormTimeSlot } from './types';

// MED-1268: the wizard saves the time slots after the cohort exists. The
// server gives each slot the next free slot number when it saves it, and
// Paperform answers match participants to slots by that number.

export interface TimeSlotFailure {
  slot: FormTimeSlot;
  // True for the slot whose request failed. False for the later slots, which
  // were not sent.
  attempted: boolean;
  error: unknown;
}

export interface TimeSlotSaveResult {
  saved: FormTimeSlot[];
  failed: TimeSlotFailure[];
}

export function toCreateTimeSlotRequest(slot: FormTimeSlot): CreateTimeSlotRequest {
  return {
    day_of_week: slot.day_of_week,
    time: slot.time.includes(':') ? slot.time : `${slot.time}:00`,
    timezone: slot.timezone,
  };
}

// Saves the slots one after another, in the order the CM entered them. It
// stops at the first failure and reports that slot and every later slot as
// unsaved. If it went on, slot 3 would get slot number 2, and a retry would
// give the failed slot number 3.
export async function createTimeSlotsInOrder(
  slots: FormTimeSlot[],
  create: (request: CreateTimeSlotRequest) => Promise<unknown>,
): Promise<TimeSlotSaveResult> {
  const saved: FormTimeSlot[] = [];
  for (let index = 0; index < slots.length; index++) {
    const slot = slots[index];
    try {
      await create(toCreateTimeSlotRequest(slot));
      saved.push(slot);
    } catch (error) {
      const failed: TimeSlotFailure[] = [
        { slot, attempted: true, error },
        ...slots.slice(index + 1).map(later => ({ slot: later, attempted: false, error: null })),
      ];
      return { saved, failed };
    }
  }
  return { saved, failed: [] };
}

export function timeSlotFailureMessage(failedCount: number, totalCount: number): string {
  const noun = totalCount === 1 ? 'time slot' : 'time slots';
  return `${failedCount} of ${totalCount} ${noun} could not be saved.`;
}

// The alert title. It says "no" when every slot failed, and "some" otherwise.
export function timeSlotFailureTitle(failedCount: number, totalCount: number): string {
  if (failedCount < totalCount) return 'Cohort created, but some time slots were not saved';
  if (totalCount === 1) return 'Cohort created, but the time slot was not saved';
  return 'Cohort created, but no time slots were saved';
}

// One count for every place that shows the tries: the first save is try 1.
export function timeSlotAttemptsText(attempts: number): string {
  return `Tried ${attempts} ${attempts === 1 ? 'time' : 'times'}.`;
}

export interface WizardNotification {
  title: string;
  message: string;
}

// The notification after a save or a retry that left slots unsaved. A failed
// retry gives the same try count as the alert in the wizard.
export function timeSlotFailureNotification(
  failedCount: number,
  totalCount: number,
  attempts: number,
): WizardNotification {
  if (attempts <= 1) {
    return {
      title: 'Time slots not saved',
      message: `The cohort was created. ${timeSlotFailureMessage(failedCount, totalCount)} The wizard lists them, with a way to retry.`,
    };
  }
  return {
    title: 'Time slots still not saved',
    message: `${timeSlotAttemptsText(attempts)} ${timeSlotFailureMessage(failedCount, totalCount)} The wizard still lists them.`,
  };
}

// A retry saved every slot. The cohort was created before, so this says only
// that the slots are saved.
export function timeSlotsSavedNotification(cohortName: string): WizardNotification {
  return {
    title: 'Time slots saved',
    message: `All time slots for ${cohortName} are saved.`,
  };
}

// The API answers 404 when the cohort no longer exists, for example when
// someone deleted it after the first try.
export function isCohortNotFoundError(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 404;
}

// QA round 5: the wizard opened with a list of unsaved slots. It checks that
// the cohort of the list still exists. Only a 404 means the cohort is gone.
// Any other error (network, server) keeps the list, and the CM can still retry.
export type CohortCheckResult = 'exists' | 'deleted' | 'unknown';

export async function checkCohortStillExists(
  cohortId: string,
  getById: (id: string) => Promise<unknown>,
): Promise<CohortCheckResult> {
  try {
    await getById(cohortId);
    return 'exists';
  } catch (error) {
    return isCohortNotFoundError(error) ? 'deleted' : 'unknown';
  }
}

// QA round 5: a delete on the cohort page says when it also cleared the list
// of unsaved time slots in this browser.
export function cohortDeleteSuccessMessage(clearedTimeSlotList: boolean): string {
  return clearedTimeSlotList
    ? 'Cohort deleted successfully. The list of unsaved time slots for this cohort is cleared.'
    : 'Cohort deleted successfully';
}

// QA round 5: logout clears the list, so the Keep List dialog says so.
export function keepListConfirmText(): string {
  return 'Keep List closes the wizard and keeps this list in this browser until you log out, so you can retry from Create Cohort later.';
}

export function cohortDeletedNotification(cohortName: string): WizardNotification {
  return {
    title: 'Cohort was deleted',
    message: `The cohort ${cohortName} no longer exists, so its time slots cannot be saved. The list of unsaved time slots is cleared.`,
  };
}

// The Review step lists the slots in the order they save, with the slot
// number each one gets.
export function reviewTimeSlotLines(slots: FormTimeSlot[]): string[] {
  return slots.map((slot, index) => `Slot ${index + 1}: ${formatTimeSlot(slot)}`);
}

// QA round 5: the lists of unsaved slots give the slot number, as Review does,
// so two slots on the same day and time read differently.
export function unsavedTimeSlotLines(timeSlots: FormTimeSlot[], unsavedSlotIds: string[]): string[] {
  const lines = reviewTimeSlotLines(timeSlots);
  return timeSlots.flatMap((slot, index) => (unsavedSlotIds.includes(slot.id) ? [lines[index]] : []));
}

// The time of the last try, with seconds, so two quick tries read differently.
export function formatLastTried(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
}

// Reads like the wizard's slot list: "Tuesday 1:30 PM ET (GMT-4)".
export function formatTimeSlot(slot: FormTimeSlot): string {
  const day = slot.day_of_week.charAt(0).toUpperCase() + slot.day_of_week.slice(1);
  const zone = getTimezoneAbbreviation(slot.timezone);
  const [hourText, minuteText = '00'] = slot.time.split(':');
  const hour = Number(hourText);
  if (Number.isNaN(hour)) {
    return `${day} ${slot.time} ${zone}`;
  }
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${day} ${hour12}:${minuteText.padStart(2, '0')} ${period} ${zone}`;
}
